import { useCallback, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { storeItem } from '#/shared/utils/storage';
import { favoriteStopsChanged } from '#/transit-tracker/slice';
import { FAVORITE_STOPS_STORAGE_KEY } from '#/transit-tracker/constants';

// TODO: PUBWEB-1796 - Import RootState type after converting transit-tracker/slice.js to TypeScript
// check src\types\StopResult.ts for reuse when converting
interface RootState {
  transitTracker: {
    favoriteStops: Array<{
      id: string | number;
      stopId: string | number;
      route: {
        id: string | number;
      };
    }>;
  };
}

type Stop = RootState['transitTracker']['favoriteStops'][number];

interface UseFavoriteStops {
  favoriteStops: Stop[];
  isFavoriteRouteStop: (stop: Stop) => boolean;
  isFavoriteStop: (stop: Stop) => boolean;
  toggleFavoriteStop: (stop: Stop) => void;
}

const useFavoriteStops = (): UseFavoriteStops => {
  const dispatch = useDispatch();

  const favoriteRouteStops = useSelector(
    (state: RootState) => state.transitTracker.favoriteStops
  );

  const [favoriteStops, setFavoriteStops] = useState<Stop[]>([]);

  useEffect(() => {
    const seen = new Set();
    setFavoriteStops(
      favoriteRouteStops.filter(el => {
        const duplicate = seen.has(el.id);
        seen.add(el.id);
        return !duplicate;
      })
    );
  }, [favoriteRouteStops]);

  const isSameFavoriteRouteStop = (stop1: Stop, stop2: Stop): boolean =>
    String(stop1.stopId) === String(stop2.stopId) &&
    String(stop1.route.id) === String(stop2.route.id);

  const isSameFavoriteStop = (stop1: Stop, stop2: Stop): boolean =>
    String(stop1.stopId) === String(stop2.stopId);

  const isFavoriteRouteStop = useCallback(
    (stop: Stop): boolean =>
      !!favoriteRouteStops &&
      favoriteRouteStops.find((fs: Stop) =>
        isSameFavoriteRouteStop(fs, stop)
      ) !== undefined,
    [favoriteRouteStops]
  );

  const isFavoriteStop = useCallback(
    (stop: Stop): boolean =>
      !!favoriteRouteStops &&
      favoriteRouteStops.find((fs: Stop) => isSameFavoriteStop(fs, stop)) !==
        undefined,
    [favoriteRouteStops]
  );

  const toggleFavoriteStop = useCallback(
    (stop: Stop): void => {
      if (isFavoriteRouteStop(stop)) {
        const newFavoriteStops = favoriteRouteStops.filter(
          (fs: Stop) => !isSameFavoriteRouteStop(fs, stop)
        );
        storeItem(FAVORITE_STOPS_STORAGE_KEY, newFavoriteStops);
        dispatch(favoriteStopsChanged(newFavoriteStops));
      } else {
        const newFavoriteStops = [...favoriteRouteStops, stop];
        storeItem(FAVORITE_STOPS_STORAGE_KEY, newFavoriteStops);
        dispatch(favoriteStopsChanged(newFavoriteStops));
      }
    },
    [dispatch, favoriteRouteStops, isFavoriteRouteStop]
  );

  return {
    favoriteStops,
    toggleFavoriteStop,
    isFavoriteStop,
    isFavoriteRouteStop,
  };
};

export default useFavoriteStops;
